// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_CUSTOM_WIDGET } from "@west/XDL/constants";
import { getScripts } from "@west/XDL/Utilities";
import React, { FC, useEffect, useRef, useState } from "react";

const enum fields {
    scriptPath = "scriptPath",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.scriptPath]: string;
    };
}

interface HubSpotAnalyticsStyles {
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
}

export const hubSpotAnalyticsStyles: HubSpotAnalyticsStyles = {};

const styles = hubSpotAnalyticsStyles;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

/**
 * We should add this widget in the Header
 * @constructor
 */
const HubSpotAnalytics: FC<OwnProps> = ({ fields }) => {
    const locationPathname = useAppSelector(state => getLocation(state).pathname);
    const [hsScriptLoaded, setHsScriptLoaded] = useState(false);

    const pathnameRef = useRef<string>(locationPathname);
    const titleRef = useRef<string>("");
    const intervalRef = useRef<number | null>(null);
    const { isInShell } = useShellContext();

    const setTracking = () => {
        if (getCookie("acceptCookies") !== "true") {
            return;
        }

        const windowIfDefined = typeof window === "undefined" ? null : (window as any);
        if (!windowIfDefined) {
            return;
        }

        windowIfDefined._hsq = windowIfDefined._hsq || [];
        windowIfDefined._hsq.push(["setPath", locationPathname]);
        windowIfDefined._hsq.push(["trackPageView"]);

        pathnameRef.current = locationPathname;
        titleRef.current = windowIfDefined.document.title;
    };

    const shouldSetTracking = () => {
        if (pathnameRef.current !== locationPathname && titleRef.current !== document.title) {
            setTracking();
            clearInterval(intervalRef.current!);
        }
    };

    useEffect(() => {
        const windowIfDefined = typeof window === "undefined" ? null : (window as any);
        if (!windowIfDefined) {
            return;
        }

        // If the HubSpot script has not loaded, load it in
        if (!hsScriptLoaded && !windowIfDefined._hsq && fields.scriptPath) {
            // If acceptCookies is missing, we must set the __hs_do_not_track cookie BEFORE loading the script
            if (getCookie("acceptCookies") !== "true") {
                const today = new Date();
                const nxt180Days = new Date(new Date().setDate(today.getDate() + 180));
                setCookie("__hs_do_not_track", "true", { expires: nxt180Days });
            }

            getScripts
                .url(fields.scriptPath, { id: "hs-script-loader", async: true })
                .then(() => setHsScriptLoaded(true));
        }

        if (pathnameRef.current === locationPathname) {
            return;
        }

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = window.setInterval(shouldSetTracking, 1000);
    }, [locationPathname]);

    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="HubSpotAnalyticsWidget">
                <Typography {...styles.title}>{translate("HubSpot Analytics")}</Typography>
                <Typography {...styles.description}>
                    {translate("Track page view and send it to hubspot analytics")}
                </Typography>
            </StyledWrapper>
        );
    }

    return null;
};

const widgetModule: WidgetModule = {
    component: HubSpotAnalytics,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                name: fields.scriptPath,
                displayName: "Script Path",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
